// src/components/portal/PortalShell.js
// ──────────────────────────────────────────────────────────────────────────
// Reusable dashboard shell for the student/staff portals: top bar with logo +
// user, a left sidebar of sections, and a content area. Sections are passed in
// so each portal defines its own nav. Scales as new sections are added.
// ──────────────────────────────────────────────────────────────────────────

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import {
  pushConfigured, pushSupported, permissionState, enablePush,
  onForegroundMessage, fetchNotifications, markAllNotificationsRead,
} from '../../lib/push';
import theme from '../../theme/theme';
import logo from '../../assets/star.png';

const Top = styled.header`
  background: ${theme.color.blueDeep}; color: #fff; padding: 0.9rem ${theme.layout.gutter};
  display: flex; align-items: center; gap: 1rem;
  .brand { display: flex; align-items: center; gap: 12px; }
  .brand img { height: 42px; width: 42px; border-radius: 50%; object-fit: cover; }
  .brand b { font-family: ${theme.font.display}; font-weight: 800; font-size: 1.1rem; }
  .brand span { display: block; font-size: 0.78rem; color: rgba(255,255,255,0.7); margin-top: -2px; }
  .right { margin-left: auto; display: flex; align-items: center; gap: 1rem; font-size: 0.85rem; color: rgba(255,255,255,0.85); }
  .right button { background: none; border: 1px solid rgba(255,255,255,0.25); color: #fff; border-radius: ${theme.layout.radius}; padding: 6px 12px; cursor: pointer; font-family: inherit; font-size: 0.82rem; }
  .right a { color: ${theme.color.lemon}; }
`;
const Layout = styled.div`
  max-width: ${theme.layout.maxWidth}; margin: 0 auto; display: grid; grid-template-columns: 250px 1fr;
  gap: 2rem; padding: 2rem ${theme.layout.gutter} 4rem; align-items: start;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;
const Side = styled.nav`
  position: sticky; top: 1.5rem; display: flex; flex-direction: column; gap: 0.4rem;
  @media (max-width: 820px) { position: static; flex-direction: row; overflow-x: auto; }
  button {
    text-align: left; background: ${(p) => p.theme}; border: 1px solid ${theme.color.line};
    border-radius: ${theme.layout.radius}; padding: 12px 16px; cursor: pointer; font-family: ${theme.font.body};
    font-size: 0.94rem; font-weight: 600; color: ${theme.color.ink}; display: flex; align-items: center; gap: 10px; white-space: nowrap;
  }
  button.active { background: ${theme.color.accent}; color: #fff; border-color: ${theme.color.accent}; }
`;
const Content = styled.main` min-height: 50vh; `;

// ── Notifications bell ──────────────────────────────────────
const Bell = styled.div`
  position: relative;
  > button.bell { position: relative; background: none; border: 1px solid rgba(255,255,255,0.25);
                  color: #fff; border-radius: ${theme.layout.radius}; padding: 6px 11px; cursor: pointer;
                  font-family: inherit; font-size: 0.95rem; line-height: 1; }
  .dot { position: absolute; top: -6px; right: -6px; background: ${theme.color.lemon}; color: ${theme.color.onLemon || '#13213D'};
         font-size: 0.65rem; font-weight: 800; min-width: 17px; height: 17px; border-radius: 999px;
         display: flex; align-items: center; justify-content: center; padding: 0 4px; }
`;
const Panel = styled.div`
  position: absolute; right: 0; top: calc(100% + 8px); width: min(340px, 88vw); z-index: 50;
  background: #fff; color: ${theme.color.ink}; border: 1px solid ${theme.color.line};
  border-radius: ${theme.layout.radius}; box-shadow: 0 12px 32px rgba(19,33,61,0.16); overflow: hidden;
  .head { display: flex; align-items: center; justify-content: space-between; padding: 0.8rem 1rem;
          border-bottom: 1px solid ${theme.color.line}; font-size: 0.9rem; font-weight: 700; }
  .head button { background: none; border: none; color: ${theme.color.accent}; font-weight: 700;
                 font-size: 0.78rem; cursor: pointer; font-family: inherit; }
  .list { max-height: 340px; overflow-y: auto; }
  .n { padding: 0.8rem 1rem; border-bottom: 1px solid ${theme.color.line}; font-size: 0.88rem; }
  .n.unread { background: #F7F9FC; }
  .n strong { display: block; font-weight: 600; }
  .n p { color: ${theme.color.inkSoft}; margin: 0.2rem 0 0; font-size: 0.84rem; }
  .n .when { color: ${theme.color.inkSoft}; font-size: 0.72rem; margin-top: 0.25rem; display: block; }
  .empty { padding: 1.4rem 1rem; color: ${theme.color.inkSoft}; font-size: 0.88rem; text-align: center; }
  .enable { padding: 0.9rem 1rem; border-top: 1px solid ${theme.color.line}; background: #F7F9FC; font-size: 0.82rem; color: ${theme.color.inkSoft}; }
  .enable button { margin-top: 0.5rem; background: ${theme.color.accent}; color: #fff; border: none;
                   font-family: inherit; font-weight: 700; font-size: 0.82rem; padding: 8px 14px;
                   border-radius: ${theme.layout.radius}; cursor: pointer; }
`;

function Notifications() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [perm, setPerm] = useState(permissionState());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(() => { fetchNotifications().then(setItems).catch(() => {}); }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh the list when a push lands while the portal is open — the service
  // worker stays quiet for foreground messages, so nothing else would show it.
  useEffect(() => {
    let stop = () => {};
    onForegroundMessage(() => load()).then((fn) => { stop = fn || (() => {}); });
    return () => stop();
  }, [load]);

  const unread = items.filter((n) => !n.read_at).length;

  const turnOn = async () => {
    setErr(''); setBusy(true);
    const res = await enablePush();
    setPerm(permissionState());
    if (!res.ok) {
      setErr(
        res.reason === 'default'
          ? 'The permission prompt was closed without choosing. Try again and click “Allow” — in Chrome it may appear as a small bell icon at the left of the address bar rather than a pop-up.'
          : res.reason === 'denied'
          ? 'Notifications are blocked for this site. Click the padlock in the address bar → Site settings → Notifications → Allow, then reload and try again.'
          : res.reason === 'unsupported' ? 'This browser can’t show push notifications. On iPhone, add this site to your home screen first.'
          : res.reason === 'not-configured' ? 'Push isn’t set up on this site yet.'
          : res.reason === 'signed-out' ? 'Please sign in again, then try.'
          : `Couldn’t turn on notifications (${res.reason}).`,
      );
    }
    setBusy(false);
  };

  const openPanel = () => {
    const next = !open;
    setOpen(next);
    if (next && unread) { markAllNotificationsRead().then(load).catch(() => {}); }
  };

  const when = (d) => {
    if (!d) return '';
    const mins = Math.round((Date.now() - new Date(d)) / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
    return new Date(d).toLocaleDateString();
  };

  return (
    <Bell>
      <button className="bell" onClick={openPanel} aria-label={`Notifications${unread ? `, ${unread} unread` : ''}`}>
        🔔{unread > 0 && <span className="dot">{unread > 9 ? '9+' : unread}</span>}
      </button>
      {open && (
        <Panel>
          <div className="head">
            <span>Notifications</span>
            <button onClick={() => setOpen(false)}>Close</button>
          </div>
          <div className="list">
            {items.length === 0
              ? <p className="empty">Nothing yet.</p>
              : items.map((n) => (
                <div key={n.id} className={`n${n.read_at ? '' : ' unread'}`}>
                  <strong>{n.title}</strong>
                  {n.body && <p>{n.body}</p>}
                  <span className="when">{when(n.created_at)}</span>
                </div>
              ))}
          </div>
          {pushConfigured && pushSupported() && perm !== 'granted' && (
            <div className="enable">
              Get alerted about new assignments, grades and announcements even when the portal is closed.
              <br />
              <button onClick={turnOn} disabled={busy}>{busy ? 'Please wait…' : 'Turn on notifications'}</button>
              {err && <p style={{ color: '#b3261e', marginTop: '0.5rem' }}>{err}</p>}
            </div>
          )}
        </Panel>
      )}
    </Bell>
  );
}
const Greeting = styled.div`
  background: ${theme.gradient.heroScrim ? theme.color.blueDeep : theme.color.blueDeep};
  background: linear-gradient(120deg, ${theme.color.accentDark}, ${theme.color.accent});
  color: #fff; border-radius: ${theme.layout.radius}; padding: 1.8rem 2rem; margin-bottom: 1.8rem;
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem;
  h1 { font-weight: 400; font-size: clamp(1.5rem, 3.5vw, 2.1rem); color: #fff; }
  p { color: rgba(255,255,255,0.85); margin-top: 0.2rem; font-size: 0.92rem; }
  .date { font-size: 0.85rem; color: rgba(255,255,255,0.8); }
`;

export default function PortalShell({ kind, sections, active, onSelect, children }) {
  const { user, role, signOut } = useAuth();
  const [, force] = useState(0);
  const today = new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const greetHour = new Date().getHours();
  const greet = greetHour < 12 ? 'Good morning' : greetHour < 17 ? 'Good afternoon' : 'Good evening';
  const name = (user?.email || '').split('@')[0];

  return (
    <>
      <Top>
        <Link to="/" className="brand">
          <img src={logo} alt="MTI" />
          <div><b>MTI</b><span>{kind} portal</span></div>
        </Link>
        <div className="right">
          <Notifications />
          <span>{user?.email} · {role}</span>
          <Link to="/set-password" style={{ color: theme.color.lemon, fontSize: '0.82rem' }}>Change password</Link>
          <button onClick={signOut}>Sign out</button>
        </div>
      </Top>

      <Layout>
        <Side>
          {sections.map((s) => (
            <button key={s.key} className={active === s.key ? 'active' : ''} onClick={() => { onSelect(s.key); force((n) => n + 1); }}>
              {s.label}
            </button>
          ))}
        </Side>
        <Content>
          <Greeting>
            <div>
              <h1>{greet}, {name}!</h1>
              <p>Welcome to your {kind} portal.</p>
            </div>
            <span className="date">{today}</span>
          </Greeting>
          {children}
        </Content>
      </Layout>
    </>
  );
}
